import React, {
    createContext,
    useContext,
    ReactNode,
    useCallback,
    useMemo
} from 'react';
import useMasterData from '../../hooks/useMasterData';
import { findRequiredCapability } from '../config/routeCapabilityMap';
import { NavigationItem, navigationSections } from '../config/navigationConfig';
import { ROUTES } from '../utils/constants';

export interface CapabilitiesContextType {
    masterData: any[];
    capabilitiesData: string[];
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    clearError: () => void;
    can: (key: string) => boolean;
    canRoute: (pathname: string) => boolean;
    getFirstAccessibleRoute: () => string;
}

const CapabilitiesContext = createContext<CapabilitiesContextType | undefined>(undefined);

interface CapabilitiesContextProps {
    children: ReactNode;
}

export const CapabilitiesProvider: React.FC<CapabilitiesContextProps> = ({ children }) => {

    const masterDataHook = useMasterData() as any;

    const capabilitiesData = useMemo(() => {
        return masterDataHook.capabilitiesData || []
    }, [masterDataHook.capabilitiesData, masterDataHook.isLoading]);

    const can = (key: string) => {
        return capabilitiesData?.includes(key);
    };

    const canRoute = (pathname: string) => {
        if (masterDataHook.isLoading && capabilitiesData.length === 0) {
            return true;
        }
        const cap = findRequiredCapability(pathname);
        if (!cap) return true;
        return can(cap);
    };

    const getFirstAccessibleRoute = useCallback((): string => {
        let navigationItems = navigationSections as any[];
        const traverse = (items: NavigationItem[]): string | null => {
            for (const item of items) {
                if (item.path === ROUTES.DASHBOARD) {
                    continue;
                }
                if (item.path && canRoute(item.path)) {
                    return item.path;
                }
                if (item.items?.length) {
                    const child = traverse(item.items);
                    if (child) return child;
                }
            }
            return null;
        };
        const route = traverse(navigationItems);
        return route ?? ROUTES.NOT_FOUND;
    }, [canRoute]);

    const contextValue = {
        ...masterDataHook,
        can,
        canRoute,
        getFirstAccessibleRoute,
    };

    return (
        <CapabilitiesContext.Provider value={contextValue}>
            {children}
        </CapabilitiesContext.Provider>
    );
};

export const useCapabilitiesContext = (): CapabilitiesContextType => {
    const context = useContext(CapabilitiesContext);
    if (context === undefined) {
        throw new Error(
            'useCapabilitiesContext must be used within a CapabilitiesProvider',
        );
    }
    return context;
};

export default CapabilitiesContext;